import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { Shirt, Sparkles, Star, MapPin, Clock, Search, ArrowLeft, Home } from 'lucide-react';

const KIND_META = {
    laundry: { label: 'Lavandería', color: 'bg-sky-100 text-sky-800', icon: Sparkles, gradient: 'from-sky-500 to-blue-600', desc: 'Lavado por kilo, paquetes básicos, recolección a domicilio.' },
    drycleaning: { label: 'Tintorería', color: 'bg-violet-100 text-violet-800', icon: Shirt, gradient: 'from-violet-500 to-fuchsia-600', desc: 'Limpieza en seco para prendas delicadas, trajes y vestidos.' },
    both: { label: 'Lavandería + Tintorería', color: 'bg-amber-100 text-amber-800', icon: Sparkles, gradient: 'from-amber-500 to-red-600', desc: '' },
};

const ProviderCard = ({ p }) => {
    const meta = KIND_META[p.kind] || KIND_META.both;
    const Icon = meta.icon;
    return (
        <Card className="overflow-hidden group" data-testid={`provider-card-${p.id}`}>
            <div className={`h-28 bg-gradient-to-br ${meta.gradient} relative`}>
                {p.banner_url && <img src={p.banner_url} alt="" className="absolute inset-0 h-full w-full object-cover" />}
                <div className="absolute top-2 left-2"><Badge className={meta.color}><Icon className="h-3 w-3 mr-1" />{meta.label}</Badge></div>
            </div>
            <CardContent className="p-4">
                <div className="flex items-center gap-3 -mt-8">
                    <div className="h-12 w-12 rounded-full bg-card border-4 border-card overflow-hidden shadow">
                        {p.logo_url ? <img src={p.logo_url} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full flex items-center justify-center bg-[hsl(var(--accent))] font-semibold">{p.name?.[0]?.toUpperCase()}</div>}
                    </div>
                </div>
                <div className="mt-2">
                    <div className="font-semibold truncate">{p.name}</div>
                    <div className="text-xs text-muted-foreground truncate">@{p.nickname}</div>
                </div>
                {p.description && <div className="text-xs text-muted-foreground mt-2 line-clamp-2">{p.description}</div>}
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-3">
                    <span className="inline-flex items-center gap-0.5"><Star className="h-3 w-3" />{(p.rating_avg || 0).toFixed(1)}</span>
                    <span className="inline-flex items-center gap-0.5"><Clock className="h-3 w-3" />{p.turnaround_hours}h</span>
                    <span className="inline-flex items-center gap-0.5"><MapPin className="h-3 w-3" />{p.zones?.slice(0, 2).join(', ') || 'CDMX'}</span>
                </div>
                <Link to={`/servicios/prestador/${p.id}`}>
                    <Button className="w-full mt-3" data-testid={`provider-select-${p.id}`}>Solicitar servicio</Button>
                </Link>
            </CardContent>
        </Card>
    );
};

export default function ServicesHubPage() {
    const nav = useNavigate();
    const [providers, setProviders] = useState([]);
    const [kind, setKind] = useState('all');
    const [zone, setZone] = useState('');
    const [q, setQ] = useState('');

    const load = useCallback(async () => {
        try {
            const params = { limit: 50 };
            if (kind !== 'all') params.kind = kind;
            if (zone) params.zone = zone;
            if (q) params.q = q;
            const { data } = await api.get('/services/providers', { params });
            setProviders(data.items || []);
        } catch (e) { console.error(e); }
    }, [kind, zone, q]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="min-h-screen" data-testid="services-hub-page">
            {/* Hero */}
            <div className="bg-gradient-to-br from-[hsl(var(--primary))] to-red-700 text-white">
                <div className="max-w-6xl mx-auto px-4 py-10">
                    <div className="flex items-center gap-2 mb-4">
                        <Button variant="ghost" size="sm" className="text-white hover:bg-white/20" onClick={() => nav(-1)} data-testid="services-back-btn">
                            <ArrowLeft className="h-4 w-4 mr-1" />Regresar
                        </Button>
                        <Link to="/">
                            <Button variant="ghost" size="sm" className="text-white hover:bg-white/20" data-testid="services-home-btn">
                                <Home className="h-4 w-4 mr-1" />Inicio
                            </Button>
                        </Link>
                    </div>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-80">
                        <Sparkles className="h-4 w-4" />Servicios a domicilio
                    </div>
                    <h1 className="text-3xl md:text-4xl font-semibold mt-2">Lavandería & Tintorería</h1>
                    <p className="text-white/80 max-w-xl mt-2">Solicita recolección en tu casa, deja el trabajo a un profesional y recibe tu ropa lista en tu puerta. Paga con tu OkWallet.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6">
                        <button onClick={() => setKind('laundry')} className={`text-left p-4 rounded-lg backdrop-blur border transition ${kind === 'laundry' ? 'bg-white text-slate-900 border-white' : 'bg-white/10 border-white/20 hover:bg-white/20'}`} data-testid="hub-select-laundry">
                            <div className="flex items-center gap-2"><Sparkles className="h-5 w-5" /><div className="font-semibold">Lavandería</div></div>
                            <div className="text-xs opacity-80 mt-1">Lavado por kilo, paquetes básicos, planchado.</div>
                        </button>
                        <button onClick={() => setKind('drycleaning')} className={`text-left p-4 rounded-lg backdrop-blur border transition ${kind === 'drycleaning' ? 'bg-white text-slate-900 border-white' : 'bg-white/10 border-white/20 hover:bg-white/20'}`} data-testid="hub-select-drycleaning">
                            <div className="flex items-center gap-2"><Shirt className="h-5 w-5" /><div className="font-semibold">Tintorería</div></div>
                            <div className="text-xs opacity-80 mt-1">Prendas delicadas, trajes, vestidos, seda.</div>
                        </button>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
                <Card>
                    <CardContent className="p-3 flex flex-wrap gap-2 items-center">
                        <div className="relative flex-1 min-w-[240px]">
                            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nombre del prestador" className="pl-9" data-testid="hub-search-name" />
                        </div>
                        <div className="relative min-w-[200px]">
                            <MapPin className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input value={zone} onChange={(e) => setZone(e.target.value)} placeholder="Zona o CP" className="pl-9" data-testid="hub-search-zone" />
                        </div>
                        <Select value={kind} onValueChange={setKind}>
                            <SelectTrigger className="w-[200px]" data-testid="hub-filter-kind"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos</SelectItem>
                                <SelectItem value="laundry">Solo Lavandería</SelectItem>
                                <SelectItem value="drycleaning">Solo Tintorería</SelectItem>
                            </SelectContent>
                        </Select>
                    </CardContent>
                </Card>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {providers.length === 0 && (
                        <div className="col-span-full text-center py-16 text-sm text-muted-foreground">
                            No hay prestadores disponibles con esos filtros aún.
                        </div>
                    )}
                    {providers.map(p => <ProviderCard key={p.id} p={p} />)}
                </div>
            </div>
        </div>
    );
}
